import React, { createContext, useEffect, useState } from "react";

import { ActualEscrow, Amortization, ServicingPayment } from "@eave/shared/types/api";
import { thereWasAProblem } from "@eave/shared/utils/alerts";

import Spinner from "web_modules/shared/Spinner";

import {
  createServicingPayment,
  deleteServicingPayment,
  getAmortizationSchedule,
  getActualEscrows,
  getSortedServicingPayments,
  createServicingActualEscrow,
  updateServicingActualEscrow,
  updateServicingPayment,
  deleteServicingActualEscrow,
} from "./api";

type PaymentAPIAction = (appId: number, payment: ServicingPayment | ActualEscrow) => Promise<{}>;

interface IAmortizationContext {
  busy: boolean;
  amortization: Amortization | null;
  explainers: string[];
  payments: ServicingPayment[] | null;
  actualEscrows: ActualEscrow[] | null;
  createPayment(payment: ServicingPayment): Promise<{}>;
  updatePayment(payment: ServicingPayment): Promise<{}>;
  deletePayment(payment: ServicingPayment): Promise<{}>;
  createActualEscrow(actualEscrow: ActualEscrow): Promise<{}>;
  updateActualEscrow(actualEscrow: ActualEscrow): Promise<{}>;
  deleteActualEscrow(actualEscrow: ActualEscrow): Promise<{}>;
}

export const AmortizationContext = createContext<IAmortizationContext>({
  busy: false,
  amortization: null,
  explainers: [],
  payments: null,
  actualEscrows: null,
  createPayment: (_: ServicingPayment) => Promise.resolve({}),
  updatePayment: (_: ServicingPayment) => Promise.resolve({}),
  deletePayment: (_: ServicingPayment) => Promise.resolve({}),
  createActualEscrow: (_: ActualEscrow) => Promise.resolve({}),
  updateActualEscrow: (_: ActualEscrow) => Promise.resolve({}),
  deleteActualEscrow: (_: ActualEscrow) => Promise.resolve({}),
});

const AmortizationProvider = ({ appId, children }) => {
  const [loading, setLoading] = useState<boolean>(true); // spinner for whole-page loads/reloads
  const [busy, setBusy] = useState<boolean>(false); // UI action lock while API request in flight

  const [amortization, setAmortization] = useState<Amortization | null>(null);
  const [explainers, setExplainers] = useState<string[]>([]);
  const [payments, setPayments] = useState<ServicingPayment[] | null>(null);
  const [actualEscrows, setActualEscrows] = useState<ActualEscrow[] | null>(null);

  const loadAmortization = () =>
    getAmortizationSchedule(appId).then(([newAmortization, newExplainers]) => {
      setAmortization(newAmortization);
      setExplainers(newExplainers);
    });
  const loadPayments = () =>
    getSortedServicingPayments(appId).then(newPayments => {
      setPayments(newPayments);
    });
  const loadActualEscrows = () =>
    getActualEscrows(appId).then(newActualEscrows => {
      setActualEscrows(newActualEscrows);
    });
  const load = () => Promise.all([loadAmortization(), loadPayments(), loadActualEscrows()]);

  useEffect(() => {
    setLoading(true);

    load()
      .catch(e => {
        thereWasAProblem("loading this page");
        throw e;
      })
      .finally(() => {
        setLoading(false);
      });
  }, [appId]);

  const apiActionThenReload = async (
    func: PaymentAPIAction,
    doingWhat: string,
    payment: ServicingPayment | ActualEscrow,
  ): Promise<{}> => {
    setBusy(true);

    try {
      await func(appId, payment); // return value unused since we need to reload anyway
      return load(); // reload to get reflowed calcs
    } catch (e) {
      thereWasAProblem(doingWhat);
      throw e;
    } finally {
      setBusy(false);
    }
  };

  const createPayment = async (payment: ServicingPayment): Promise<{}> =>
    apiActionThenReload(createServicingPayment, "creating this payment", payment);

  const updatePayment = async (payment: ServicingPayment): Promise<{}> =>
    apiActionThenReload(updateServicingPayment, "updating this payment", payment);

  const deletePayment = async (payment: ServicingPayment): Promise<{}> =>
    apiActionThenReload(deleteServicingPayment, "deleting this payment", payment);

  const createActualEscrow = (actualEscrow: ActualEscrow): Promise<{}> =>
    apiActionThenReload(createServicingActualEscrow, "creating this actual escrow", actualEscrow);

  const updateActualEscrow = (actualEscrow: ActualEscrow): Promise<{}> =>
    apiActionThenReload(updateServicingActualEscrow, "updating this actual escrow", actualEscrow);

  const deleteActualEscrow = async (actualEscrow: ActualEscrow): Promise<{}> =>
    apiActionThenReload(deleteServicingActualEscrow, "deleting this escrow", actualEscrow);

  if (loading) {
    return <Spinner active />;
  }

  return (
    <AmortizationContext.Provider
      value={{
        busy,
        amortization,
        explainers,
        payments,
        actualEscrows,
        createPayment,
        updatePayment,
        deletePayment,
        createActualEscrow,
        updateActualEscrow,
        deleteActualEscrow,
      }}
    >
      {children}
    </AmortizationContext.Provider>
  );
};

export default AmortizationProvider;
